/**
 * Telemetry processing utilities for creating indexed data structures
 */

import {
  ChartIndexedTelemetry,
  TelemetryResolution,
  ChartTelemetryStatistics,
} from '../types/TelemetryTypes'
import { GpsPoint } from '../domain/telemetry/processed/GpsPoint'
import { roundDistance } from '../utils/distanceUtils'
import { roundTime } from '../utils/timeUtils'
import { MS_PER_S } from '../utils/timeUnits'
import { maxOf } from '../utils/extremes'

/**
 * Creates indexed telemetry data structure from raw points
 * Used by chart processors for backward compatibility with existing chart code.
 * @param points Raw telemetry points
 * @returns Indexed telemetry with lookup maps
 */
export function createLegacyIndexedTelemetry(
  points: GpsPoint[]
): ChartIndexedTelemetry {
  const distanceIndex: Record<number, number> = {}
  const timeIndex: Record<number, number> = {}

  // Build indices
  points.forEach((point, index) => {
    const roundedDistance = roundDistance(point.distanceMeters, 5)
    const roundedTime = roundTime(point.elapsedMs, 100)

    // Store the first occurrence of each rounded value
    if (!(roundedDistance in distanceIndex)) {
      distanceIndex[roundedDistance] = index
    }
    if (!(roundedTime in timeIndex)) {
      timeIndex[roundedTime] = index
    }
  })

  const statistics = calculateStatistics(points)

  return {
    points,
    distanceIndex,
    timeIndex,
    statistics,
  }
}

/**
 * Calculates statistical summary of telemetry data
 */
function calculateStatistics(points: GpsPoint[]): ChartTelemetryStatistics {
  if (points.length === 0) {
    return {
      maxSpeed: 0,
      avgSpeed: 0,
      totalDistance: 0,
      totalTimeMs: 0,
      pointCount: 0,
      frequency: 0,
    }
  }

  const maxSpeed = maxOf(points.map((p) => p.speedKmh))
  const avgSpeed =
    points.reduce((sum, p) => sum + p.speedKmh, 0) / points.length
  const totalDistance = points[points.length - 1].distanceMeters
  const totalTimeMs = points[points.length - 1].elapsedMs
  const frequency = points.length / (totalTimeMs / MS_PER_S)

  return {
    maxSpeed: Math.round(maxSpeed * 100) / 100,
    avgSpeed: Math.round(avgSpeed * 100) / 100,
    totalDistance: Math.round(totalDistance),
    totalTimeMs,
    pointCount: points.length,
    frequency: Math.round(frequency * 10) / 10,
  }
}

/**
 * Creates a decimated (reduced) version of telemetry points
 * @param points Full resolution points
 * @param interval Distance interval in meters
 * @returns Decimated points
 */
export function decimateTelemetryByDistance(
  points: GpsPoint[],
  interval: number
): GpsPoint[] {
  if (points.length === 0) return []

  const decimated: GpsPoint[] = [points[0]] // Always include first point
  let lastDistance = points[0].distanceMeters

  for (let i = 1; i < points.length; i++) {
    const point = points[i]
    if (point.distanceMeters - lastDistance >= interval) {
      decimated.push(point)
      lastDistance = point.distanceMeters
    }
  }

  // Always include last point if not already included
  const lastPoint = points[points.length - 1]
  if (decimated[decimated.length - 1] !== lastPoint) {
    decimated.push(lastPoint)
  }

  return decimated
}

/**
 * Creates multi-resolution telemetry data
 * @param fullPoints Full resolution telemetry points
 * @returns Multi-resolution data structure
 */
export function createMultiResolution(fullPoints: GpsPoint[]): {
  high: TelemetryResolution
  medium: TelemetryResolution
  low: TelemetryResolution
} {
  // High resolution: every 5 meters or all points if denser
  const highPoints =
    fullPoints.length > 1000
      ? decimateTelemetryByDistance(fullPoints, 5)
      : fullPoints

  // Medium resolution: every 25 meters
  const mediumPoints = decimateTelemetryByDistance(fullPoints, 25)

  // Low resolution: every 100 meters
  const lowPoints = decimateTelemetryByDistance(fullPoints, 100)

  return {
    high: {
      resolution: 'high',
      interval: 5,
      data: createLegacyIndexedTelemetry(highPoints),
    },
    medium: {
      resolution: 'medium',
      interval: 25,
      data: createLegacyIndexedTelemetry(mediumPoints),
    },
    low: {
      resolution: 'low',
      interval: 100,
      data: createLegacyIndexedTelemetry(lowPoints),
    },
  }
}

// /**
//  * Interpolates a telemetry point at a specific distance
//  * @param targetDistance Target distance
//  * @param points Telemetry points
//  * @returns Interpolated point or null if not possible
//  */
// export function interpolateAtDistance(
//   targetDistance: number,
//   points: TelemetryPoint[]
// ): TelemetryPoint | null {
//   if (points.length === 0) return null

//   // Find bracketing points
//   let beforeIndex = -1
//   let afterIndex = -1

//   for (let i = 0; i < points.length - 1; i++) {
//     if (
//       points[i].distanceTraveled <= targetDistance &&
//       points[i + 1].distanceTraveled >= targetDistance
//     ) {
//       beforeIndex = i
//       afterIndex = i + 1
//       break
//     }
//   }

//   // If exact match, return it
//   if (
//     beforeIndex >= 0 &&
//     points[beforeIndex].distanceTraveled === targetDistance
//   ) {
//     return points[beforeIndex]
//   }
//   if (
//     afterIndex >= 0 &&
//     points[afterIndex].distanceTraveled === targetDistance
//   ) {
//     return points[afterIndex]
//   }

//   // If not found, return null
//   if (beforeIndex === -1 || afterIndex === -1) return null

//   const before = points[beforeIndex]
//   const after = points[afterIndex]

//   // Linear interpolation
//   const ratio =
//     (targetDistance - before.distanceTraveled) /
//     (after.distanceTraveled - before.distanceTraveled)

//   return {
//     latitude: before.latitude + (after.latitude - before.latitude) * ratio,
//     longitude: before.longitude + (after.longitude - before.longitude) * ratio,
//     speed: before.speed + (after.speed - before.speed) * ratio,
//     timestamp: new Date(
//       before.timestamp.getTime() +
//         (after.timestamp.getTime() - before.timestamp.getTime()) * ratio
//     ),
//     distanceTraveled: targetDistance,
//     elapsedMs: before.elapsedMs + (after.elapsedMs - before.elapsedMs) * ratio,
//     timeString: '', // Will be formatted by caller
//     leanAngle:
//       before.leanAngle && after.leanAngle
//         ? before.leanAngle + (after.leanAngle - before.leanAngle) * ratio
//         : undefined,
//     acceleration:
//       before.acceleration && after.acceleration
//         ? before.acceleration +
//           (after.acceleration - before.acceleration) * ratio
//         : undefined,
//   }
// }

// /**
//  * Smooths telemetry data using moving average
//  * @param points Telemetry points
//  * @param windowSize Window size for smoothing
//  * @param property Property to smooth
//  * @returns Points with smoothed values
//  * Note: GpsPoint needs to be extended with optional leanAngle and acceleration fields
//  */
// export function smoothTelemetry(
//   points: GpsPoint[],
//   windowSize: number,
//   property: 'speedKmh' // Simplified until GpsPoint is extended with optional fields
// ): GpsPoint[] {
//   const halfWindow = Math.floor(windowSize / 2)

//   return points.map((point, index) => {
//     const start = Math.max(0, index - halfWindow)
//     const end = Math.min(points.length, index + halfWindow + 1)
//     const window = points.slice(start, end)

//     const values = window
//       .map((p) => p[property])
//       .filter((v): v is number => v !== undefined)

//     if (values.length === 0) return point

//     const avg = values.reduce((sum, v) => sum + v, 0) / values.length

//     return {
//       ...point,
//       [property]: avg,
//     }
//   })
// }
